import { useEffect, useRef } from 'react'
import type { AuditRun } from '../api/enrollment-overlap.types'
import { isClientError } from '../utils/api-errors'
import { isRunStalled } from '../utils/run-display'
import { useRunQuery } from './useEnrollmentOverlapQueries'

/**
 * Polls `GET /runs/:runId` every ~5s until the run leaves RUNNING, then calls
 * `onFinished` once for that run. Polling also stops when the run can
 * not be read (4xx), when it has been RUNNING long enough to count as stalled
 * (abandoned; it will be replaced by the next audit), and on unmount.
 *
 * `now` must advance over time (see `useNow`): a stalled run's response does
 * not change, so nothing else would re-run the stall check.
 *
 * Pass `null` to not poll.
 */
export function useRunPolling(
  runId: string | null,
  onFinished: (run: AuditRun | null) => void,
  now: number = Date.now()
) {
  const query = useRunQuery(runId, { poll: true })
  const onFinishedRef = useRef(onFinished)
  const finishedRunIdRef = useRef<string | null>(null)

  useEffect(() => {
    onFinishedRef.current = onFinished
  }, [onFinished])

  const status = query.data?.status
  const isUnreadable = Boolean(query.error) && isClientError(query.error)
  const isStalled = query.data ? isRunStalled(query.data, now) : false
  const hasFinished =
    (status !== undefined && status !== 'RUNNING') || isUnreadable || isStalled

  useEffect(() => {
    if (runId === null) {
      // Tracking ended; a later tracking session of any run starts fresh.
      finishedRunIdRef.current = null
      return
    }
    if (!hasFinished) return

    // A status the server reports as final (or an unreadable run) is reported
    // once per run: the cached result can be seen again while the same run is
    // still on screen. Stalled is only the client's reading of RUNNING data,
    // so it is never remembered. The same run can be tracked again (e.g. the
    // backend answers "already in progress" with it) and must be released again.
    if (!isStalled) {
      if (finishedRunIdRef.current === runId) return
      finishedRunIdRef.current = runId
    }
    onFinishedRef.current(query.data ?? null)
  }, [runId, hasFinished, isStalled, query.data])

  return query
}
